import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Security } from "../proto/generated/security";
import { Position } from "../proto/generated/position";
import { getSecurityKey } from "../util/security";

export interface PositionsStateType {
    loading: boolean,
    loaded: boolean,
    positions: Position[],
    groupBy: 'strategyGroup' | 'basket' | 'symbol'
}

const initialState: PositionsStateType = {
    loading: false,
    loaded: false,
    positions: [],
    groupBy: 'basket'
};

export type GroupedPosition = {
    groupId: string,
    groupName?: string,
    quantity: number,
    avgPrice: number,
    sqOffPrice?: number,
    realizedProfit: number,
    security: Security,
    originalPosition?: Position,
    children?: GroupedPosition[]
}

export type AnalyzeGroupedPosition = Omit<GroupedPosition, "children"> & {
    new?: boolean,
    squaredOffQuantity?: number,
    addedQuantity?: number,
    newQuantity?: number,
    squareOffOrderId?: string,
    addOrderId?: string,
    children: AnalyzeGroupedPosition[]
};

const positionsSlice = createSlice({
    name: 'positions',
    initialState,
    reducers: {
        setLoading(state: PositionsStateType, action: PayloadAction<PositionsStateType['loading']>) {
            return {
                ...state,
                loading: action.payload
            }
        },
        updatePositions(state, action: PayloadAction<{ positions: Position[], overwrite?: boolean}>) {
            const { overwrite, positions } = action.payload;
            if (overwrite) {
                return {
                    ...state,
                    loaded: true,
                    positions
                }
            }

            const positionsMap = positions.reduce((acc, p) => {
                const posId = `${p.basketId}-${getSecurityKey(p.security.exchange, p.security.code)}`;
                return ({...acc, [posId]: p})
            }, {} as Record<string, Position>)
            const existingPositionsMap = state.positions.reduce((acc, p) => {
                const posId = `${p.basketId}-${getSecurityKey(p.security.exchange, p.security.code)}`;
                return ({...acc, [posId]: p})
            }, {} as Record<string, Position>)
            const newPositions = state.positions.map(p => {
                const posId = `${p.basketId}-${getSecurityKey(p.security.exchange, p.security.code)}`;
                return positionsMap[posId] || p
            })
            Object.entries(positionsMap).forEach(([posId, p]) => {
                if (!existingPositionsMap[posId]) {
                    newPositions.push(p)
                }
            })

            return {
                ...state,
                loaded: true,
                positions: newPositions
            }
        },
        setGroupBy(state, action: PayloadAction<PositionsStateType['groupBy']>) {
            return {
                ...state,
                groupBy: action.payload
            }
        },
    }
})

const { actions, reducer } = positionsSlice

export const { setLoading, updatePositions, setGroupBy } = actions

export default reducer
